import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import AuthPortalIllustration from "../../assets/images/auth-portal-illustration.png";
import NoPrimaryOrgBackground from "../../assets/images/no-primary-org-bg.svg";
import NoPrimaryOrgBackgroundSvg from "../../assets/images/no-primary-org-grid.svg";
import CubesBg from "../../assets/images/cubesBg.png";
import OrgIcon from "../../assets/icons/OrgIcon";
import CrossIcon from "../../assets/icons/CrossIcon";
import JoinOrganizationCard from "./JoinOrganizationCard";
import ApprovalPendingCard from "./ApprovalPendingCard";
import FormFiller from "../../components/FormBuilder/CustomForm/FormFiller";
import { authFetch } from "../../utils/authFetch";
import { useDispatch, useSelector } from "react-redux";
import { selectAuth, setAuth } from "../auth/authSlice";
import ProfileDropdown from "./ProfileDropdown";
import ChevronDownIcon from "../../assets/icons/ChevronDownIcon";
import OnboardingAnnouncementBar from "./OnboardingAnnouncementBar";

interface Organization {
  id: string;
  name: string;
  requiresApproval: boolean;
  signedUrl?: string;
  membershipStatusId?: 0 | 1 | 14 | 15 | 16 | "none";
  joinRequest?: {
    id?: number;
    requestedAt?: string;
    reviewedAt?: string;
    reviewNotes?: string | null;
  };
}

interface NoPrimaryOrganizationViewProps {
  fullName?: string;
  email?: string;
  profileImageUrl?: string;
  logoUrl?: string;
  profileCompletionPercentage?: number;
  contactProfileCompleted?: boolean | null;
  onSkipToDashboard?: () => void;
  onCompleteProfile?: () => void;
  onEnterOnboardingIntro?: () => void;
  renderInModal?: boolean;
  onCloseModal?: () => void;
  onCreateOrganizationSuccess?: () => void;
}

const NoPrimaryOrganizationView: React.FC<NoPrimaryOrganizationViewProps> = ({
  fullName,
  email,
  profileImageUrl,
  logoUrl,
  profileCompletionPercentage: incomingProfileCompletionPercentage = 0,
  contactProfileCompleted,
  onSkipToDashboard,
  onCompleteProfile,
  onEnterOnboardingIntro,
  renderInModal = false,
  onCloseModal,
  onCreateOrganizationSuccess,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const initialOrganizationIdRef = useRef<string | null>(null);
  const [view, setView] = useState<"main" | "join" | "approval-pending">("main");
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [showCreateOrgForm, setShowCreateOrgForm] = useState(false);
  const [showOnboardingIntro, setShowOnboardingIntro] = useState(false);
  const [localProfileCompletionPercentage, setLocalProfileCompletionPercentage] = useState<number | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const effectiveProfileCompletionPercentage =
    localProfileCompletionPercentage ?? incomingProfileCompletionPercentage;

  useEffect(() => {
    if (initialOrganizationIdRef.current != null) {
      return;
    }

    if (auth?.organizationId != null) {
      initialOrganizationIdRef.current = String(auth.organizationId);
    }
  }, [auth?.organizationId]);

  const handleJoinClick = () => {
    setView("join");
  };

  const handleBackClick = () => {
    setView("main");
  };

  const handleOrganizationJoinClick = (organization: Organization) => {
    setSelectedOrganization(organization);
    setView("approval-pending");
  };

  const handleBackToWebsite = () => {
    setView("main");
    setSelectedOrganization(null);
  };

  const handleSwitchOrganization = () => {
    setView("join");
    setSelectedOrganization(null);
  };

  const handleOrganizationCreated = async () => {
    setShowCreateOrgForm(false);

    if (renderInModal) {
      onCreateOrganizationSuccess?.();
      return;
    }

    try {
      const apiBase = process.env.REACT_APP_API_URL;
      if (!apiBase) {
        onSkipToDashboard?.();
        return;
      }

      const response = await authFetch(`${apiBase}/portal/me`);
      const json: any = await response.json();

      if (response.ok) {
        const user = json?.data ?? json;
        const nextAuth = {
          ...auth,
          email: user?.email ?? auth?.email ?? null,
          userId: user?.id ?? auth?.userId ?? null,
          firstName: user?.firstName ?? auth?.firstName ?? null,
          lastName: user?.lastName ?? auth?.lastName ?? null,
          fullName: user?.fullName ?? auth?.fullName ?? null,
          organizationId: user?.primaryOrganization?.id ?? auth?.organizationId ?? null,
        };

        dispatch(setAuth(nextAuth as any));
      }

      const completed =
        json?.data?.contactProfileCompleted ?? json?.contactProfileCompleted;
      const pctRaw =
        json?.data?.profileCompletionPercentage ?? json?.profileCompletionPercentage ?? 0;
      const pct = typeof pctRaw === "number" && !Number.isNaN(pctRaw) ? pctRaw : 0;
      setLocalProfileCompletionPercentage(pct);

      if (completed === false) {
        setShowOnboardingIntro(true);
        onEnterOnboardingIntro?.();
      } else {
        onSkipToDashboard?.();
      }
    } catch {
      onSkipToDashboard?.();
    }
  };

  const mapOrganization = (item: any): Organization | null => {
    const idValue = item?.id ?? item?.organizationId ?? item?.orgId;
    const nameValue = item?.name ?? item?.organizationName ?? item?.title;

    if (idValue == null || !nameValue) {
      return null;
    }

    const actions = item?.actions;
    const statusIdValue =
      typeof item?.membershipStatusId === "number"
        ? item.membershipStatusId
        : item?.membershipStatus === "pending"
          ? 1
          : item?.membershipStatus === "accepted"
            ? 14
            : item?.membershipStatus === "rejected"
              ? 15
              : item?.membershipStatus === "joined" || item?.membershipStatus === "member"
                ? 16
                : item?.membershipStatus === "none"
                  ? 0
                  : undefined;

    return {
      id: String(idValue),
      name: String(nameValue),
      requiresApproval: Boolean(
        item?.requiresApproval ??
        item?.approvalRequired ??
        item?.requires_approval ??
        actions?.requiresApproval ??
        actions?.approvalRequired
      ),
      signedUrl: typeof item?.signedUrl === "string" ? item.signedUrl : "",
      membershipStatusId:
        statusIdValue === 0 || statusIdValue === 1 || statusIdValue === 14 || statusIdValue === 15 || statusIdValue === 16
          ? statusIdValue
          : item?.membershipStatusId === "none"
            ? "none"
            : undefined,
      joinRequest: item?.joinRequest
        ? {
          id: item.joinRequest.id,
          requestedAt: item.joinRequest.requestedAt,
          reviewedAt: item.joinRequest.reviewedAt,
          reviewNotes: item.joinRequest.reviewNotes ?? null,
        }
        : undefined,
    };
  };

  const refreshPortalStatus = useCallback(async () => {
    const apiBase = process.env.REACT_APP_API_URL;
    if (!apiBase) {
      return;
    }

    try {
      const meResponse = await authFetch(`${apiBase}/portal/me`);
      const meJson: any = await meResponse.json();

      if (meResponse.ok) {
        const user = meJson?.data ?? meJson;
        const nextAuth = {
          ...auth,
          email: user?.email ?? auth?.email ?? null,
          userId: user?.id ?? auth?.userId ?? null,
          firstName: user?.firstName ?? auth?.firstName ?? null,
          lastName: user?.lastName ?? auth?.lastName ?? null,
          fullName: user?.fullName ?? auth?.fullName ?? null,
          organizationId: user?.primaryOrganization?.id ?? auth?.organizationId ?? null,
        };

        dispatch(setAuth(nextAuth as any));

        const pctRaw =
          meJson?.data?.profileCompletionPercentage ?? meJson?.profileCompletionPercentage ?? 0;
        const pct = typeof pctRaw === "number" && !Number.isNaN(pctRaw) ? pctRaw : 0;
        setLocalProfileCompletionPercentage(pct);

        const primaryOrgIdRaw = user?.primaryOrganization?.id;
        const primaryOrgId = primaryOrgIdRaw != null ? String(primaryOrgIdRaw) : null;
        if (primaryOrgId) {
          const initialOrgId = initialOrganizationIdRef.current;
          const isOnboardingNoOrg = !renderInModal || !initialOrgId;
          const hasSwitchedToSelectedOrg =
            !!selectedOrganization?.id &&
            primaryOrgId === selectedOrganization.id &&
            (!initialOrgId || primaryOrgId !== initialOrgId);

          if (isOnboardingNoOrg || hasSwitchedToSelectedOrg) {
            onSkipToDashboard?.();
            return;
          }
        }
      }

      if (selectedOrganization?.id) {
        const params = new URLSearchParams({ page: "1", limit: "100" });
        const orgResponse = await authFetch(
          `${apiBase}/portal/organizations?${params.toString()}`
        );
        const orgJson: any = await orgResponse.json();

        if (orgResponse.ok) {
          const source: any[] =
            orgJson?.organizations ??
            orgJson?.data?.organizations ??
            orgJson?.data ??
            orgJson?.results ??
            [];

          const matched = Array.isArray(source)
            ? source
              .map(mapOrganization)
              .find((org): org is Organization => !!org && org.id === selectedOrganization.id)
            : undefined;

          if (matched) {
            setSelectedOrganization(matched);

            if (matched.membershipStatusId === 14) {
              onSkipToDashboard?.();
            }
          }
        }
      }
    } catch {
      // Ignore transient refresh errors; user can retry.
    }
  }, [auth, dispatch, onSkipToDashboard, selectedOrganization]);

  const mainContent = (
    <>
      {showOnboardingIntro ? (
        <>
          <Title>
            Hello, <Accent>{fullName || "there"}</Accent>!
          </Title>
          <Heading>Your journey begins now</Heading>
          <Subtitle>
            Your journey starts here—explore, connect, and make an impact. We’re
            excited to have you as part of the team!
          </Subtitle>

          <OnboardingActions>
            <OnboardingSecondaryButton type="button" onClick={onSkipToDashboard}>
              Skip To Dashboard
            </OnboardingSecondaryButton>
            <OnboardingPrimaryButton type="button" onClick={onCompleteProfile}>
              Complete Your Profile
            </OnboardingPrimaryButton>
          </OnboardingActions>
        </>
      ) : (
        <>
          <Title>
            Hello, <Accent>{fullName || "there"}</Accent>!
          </Title>
          <Heading>Welcome To Member things L3 Portal</Heading>
          <Subtitle>
            To get started, we need to place you in an organization. You can create
            a new organization or join an existing organization.
          </Subtitle>

          <ActionsRow>
            <ActionCardButton onClick={handleJoinClick}>
              <IconWrapper>
                <OrgIcon size={32} color="#003366" />
              </IconWrapper>
              <ActionTitle>Join Existing Organization</ActionTitle>
              <ActionBody>
                Find your organization or team and request access. Your L3 admin will
                review your request.
              </ActionBody>
            </ActionCardButton>

            <ActionCardButton onClick={() => setShowCreateOrgForm(true)}>
              <IconWrapper>
                <OrgIcon size={32} color="#003366" />
              </IconWrapper>
              <ActionTitle>Create New Organization</ActionTitle>
              <ActionBody>
                Starting something fresh? Register a new L3 entity under an existing
                L2 umbrella.
              </ActionBody>
            </ActionCardButton>
          </ActionsRow>
        </>
      )}
    </>
  );

  const modalMainContent = (
    <ModalChooser>
      <ModalChooserTitle>Choose You Get Into An Organization</ModalChooserTitle>
      <ModalChooserSubtitle>
        To get started, we need to place you in an organization. You can create new
        organization and join and existing organization.
      </ModalChooserSubtitle>

      <ModalActionsRow>
        <ModalActionCardButton onClick={handleJoinClick}>
          <ModalIconWrapper>
            <OrgIcon size={32} color="#003366" />
          </ModalIconWrapper>
          <div>
            <ActionTitle>Join Existing Organization</ActionTitle>
            <ActionBody>
              Find your organization or team and request access. Your L3 admin will
              review your request.
            </ActionBody>
          </div>
        </ModalActionCardButton>

        <ModalActionCardButton onClick={() => setShowCreateOrgForm(true)}>
          <ModalIconWrapper>
            <OrgIcon size={32} color="#003366" />
          </ModalIconWrapper>
          <div>
            <ActionTitle>Create New Organization</ActionTitle>
            <ActionBody>
              Starting something fresh? Register a new L3 entity under an existing
              L2 umbrella.
            </ActionBody>
          </div>
        </ModalActionCardButton>
      </ModalActionsRow>
    </ModalChooser>
  );

  const flowContent = (
    <>
      {view === "main" ? (
        renderInModal ? (
          modalMainContent
        ) : (
          <Card>
            <Avatar src={AuthPortalIllustration} alt="Member portal illustration" />
            {mainContent}
          </Card>
        )
      ) : view === "join" ? (
        <JoinOrganizationCard
          onBack={handleBackClick}
          onJoin={handleOrganizationJoinClick}
          onRefreshStatus={refreshPortalStatus}
          inModal={renderInModal}
        />
      ) : (
        <ApprovalPendingCard
          organizationName={selectedOrganization?.name || ""}
          organizationId={selectedOrganization?.id}
          membershipStatusId={selectedOrganization?.membershipStatusId}
          joinRequest={selectedOrganization?.joinRequest}
          inModal={renderInModal}
          onBackToWebsite={handleBackToWebsite}
          onSwitchOrganization={handleSwitchOrganization}
          onRefreshStatus={refreshPortalStatus}
        />
      )}
    </>
  );

  const modalInnerContent = showCreateOrgForm ? (
    <FormFiller
      formType="organization"
      inlineMode
      onClose={() => setShowCreateOrgForm(false)}
      onSuccess={handleOrganizationCreated}
      allowFormSwitch={false}
      hideFormTitle
      hidePagesTitles
    />
  ) : (
    flowContent
  );

  if (renderInModal) {
    return (
      <ModalOverlay>
        <ModalPanel>
          <ModalPageHeader>
            <ModalPageTitle>Add New Organization</ModalPageTitle>
          </ModalPageHeader>

          <ModalBody>
            <ModalOuterCard>
              <ModalFrame>
                <ModalFrameInner>{modalInnerContent}</ModalFrameInner>
              </ModalFrame>
            </ModalOuterCard>
          </ModalBody>

          {!showCreateOrgForm && (
            <ModalFooter>
              <ModalFooterCloseButton type="button" onClick={onCloseModal || onSkipToDashboard}>
                <CrossIcon size={18} />
                Close
              </ModalFooterCloseButton>
            </ModalFooter>
          )}
        </ModalPanel>
      </ModalOverlay>
    );
  }

  return (
    <>
      <HeaderBar>
        <HeaderLeft>
          {logoUrl && <HeaderLogoImage src={logoUrl} alt={fullName || "Logo"} />}
        </HeaderLeft>
        <HeaderRight>
          <ProfileTriggerButton
            type="button"
            onClick={() => setIsProfileOpen((prev) => !prev)}
          >
            <ProfileAvatarCircle>
              {profileImageUrl ? (
                <ProfileAvatarImage src={profileImageUrl} alt={fullName || "Profile"} />
              ) : (
                (fullName || auth.fullName || auth.email || "User").charAt(0).toUpperCase()
              )}
            </ProfileAvatarCircle>
            <ProfileTriggerText>{fullName || auth.fullName || "Member"}</ProfileTriggerText>
            <ChevronDownIcon color="#6b7280" />
          </ProfileTriggerButton>
          {isProfileOpen && (
            <DropdownWrapper>
              <ProfileDropdown
                name={fullName || auth.fullName || undefined}
                email={email || auth.email || undefined}
                imageUrl={profileImageUrl}
                showMenuItems={false} />
            </DropdownWrapper>
          )}
        </HeaderRight>
      </HeaderBar>
      {contactProfileCompleted === false && onCompleteProfile && (
        <OnboardingAnnouncementBar
          value={effectiveProfileCompletionPercentage}
          onClick={onCompleteProfile}
        />
      )}
      <Backdrop>
        <RaysLayer />
        {showCreateOrgForm && (
          <FormFiller
            formType="organization"
            onClose={() => setShowCreateOrgForm(false)}
            onSuccess={handleOrganizationCreated}
            allowFormSwitch={false}
             />
            
        )}
        {flowContent}
      </Backdrop></>
  );
};

const Backdrop = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  background-color: #f7f8fb;
  background-image: url(${NoPrimaryOrgBackgroundSvg});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: right;
  align-items: stretch;
  background: rgba(0, 0, 0, 0.5);
`;

const ModalPanel = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 43vw;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1440px) {
    max-width: 45vw;
  }
`;

const ModalPageHeader = styled.div`
  padding: 28px 34px 14px;
  background-image: url(${CubesBg});
  background-repeat: repeat;
  background-position: center top;
  background-size: contain;
  background-color: #ffffff;
`;

const ModalPageTitle = styled.h2`
  margin: 0;
  color: #003366;
  font-size: 22px;
  font-weight: 600;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0px 34px 0px;
  background: #ffffff;
`;

const ModalOuterCard = styled.div`
  border: 1px solid #eef2f7;
  border-radius: 4px;
  padding: 18px;
  background: #ffffff;
`;

const ModalFrame = styled.div`
  border: 2px solid #0b52cf;
  border-left: 4px solid #0b52cf;
  border-top-left-radius: 4px;
  padding: 22px;
  background: #f6f9ff;
`;

const ModalFrameInner = styled.div`
  border: 1px dashed #d1d5db;
  border-radius:4px;
  padding: 16px 16px 16px;
  background: #ffffff;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 18px 50px 24px;
  background: #ffffff;
`;

const ModalFooterCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  border: 1px solid #BABDC1;
  border-radius: 4px;
  background: #ffffff;
  color: #585558;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

const HeaderBar = styled.header`
  // position: absolute;
  // top: 0;
  // left: 0;
  // right: 0;
  height: 72px;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 5;
  background: #fdfdff;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderLogoText = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: #003366;
`;

const HeaderLogoImage = styled.img`
  height: 40px;
  width: auto;
  display: block;
`;

const HeaderRight = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ProfileTriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: none;
  background: transparent;
  // box-shadow: 0 8px 24px rgba(15, 59, 112, 0.12);
  cursor: pointer;
`;

const ProfileAvatarCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
`;

const ProfileAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const ProfileTriggerText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const DropdownWrapper = styled.div`
  position: absolute;
  top: 0px;
  right: 0;
`;

const RaysLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${NoPrimaryOrgBackground});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  z-index: 1;
  pointer-events: none;
`;

const Card = styled.div`
  position: relative;
  z-index: 3;
  width: min(760px, calc(100% - 48px));
  padding: 34px 34px 30px;
  text-align: center;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.97);
`;

const Avatar = styled.img`
  width: 126px;
  height: auto;
  display: block;
  margin: 0 auto 16px;
`;

const ModalChooser = styled.div`
  max-width: 680px;
  margin: 0 auto;
  text-align: center;
`;

const ModalChooserTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #003366;
`;

const ModalChooserSubtitle = styled.p`
  margin: 12px auto 0;
  max-width: 520px;
  color: #6b778a;
  font-size: 14px;
  line-height: 1.6;
`;

const ModalActionsRow = styled.div`
  margin-top: 28px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
`;

const ModalActionCardButton = styled.button`
  width: 100%;
  border: 1px solid #d9e7ff;
  border-radius: 12px;
  padding: 22px;
  text-align: left;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  display: grid;
  grid-template-columns: 56px 1fr;
  align-items: start;
  gap: 18px;

  &:hover {
    border-color: #0b52cf;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(15, 70, 128, 0.08);
  }
`;

const ModalIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e6eefc;
  border-radius: 8px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 1.15;
  color: #003366;
  font-weight: 600;
`;

const Accent = styled.span`
  color: #ff6f3f;
`;

const Heading = styled.p`
  margin: 8px 0 0;
  font-size: 24px;
  line-height: 1.15;
  color: #003366;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 18px auto 0;
  max-width: 650px;
  color: #6b778a;
  font-size: 14px;
  line-height: 1.7;
`;

const ActionsRow = styled.div`
  margin-top: 30px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const IconWrapper = styled.div`
  display: flex;
//   justify-content: center;
  margin-bottom: 12px;
  padding: 8px;
  background: #fff;
  width: 48px;
  border-radius: 8px;
`;

const ActionCard = styled.div`
  border: 1px solid #e5ecf5;
  border-radius: 10px;
  padding: 20px;
  text-align: left;
  background: #f0f6fb;
`;

const ActionCardButton = styled(ActionCard)`
  cursor: pointer;
  transition: all 0.2s ease;
    
  &:hover {
    border-color: #003366;
    background: #f0f6fb;
    box-shadow: 0 2px 8px rgba(15, 70, 128, 0.08);
  }
`;

const ActionTitle = styled.h3`
  margin: 0;
  color: #003366;
  font-size: 16px;
  line-height: 1.3;
  font-weight: 600;
`;

const ActionBody = styled.p`
  margin: 12px 0 0;
  color: #5d6f87;
  font-size: 14px;
  line-height: 1.6;
`;

const OnboardingActions = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const OnboardingPrimaryButton = styled.button`
  min-width: 220px;
  padding: 12px 24px;
  border-radius: 6px;
  border: none;
  background-color: #003366;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const OnboardingSecondaryButton = styled.button`
  min-width: 220px;
  padding: 12px 24px;
  border-radius: 6px;
  border: 1px solid #e5ecf5;
  background-color: #f5f7fb;
  color: #1b2838;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

export default NoPrimaryOrganizationView;
